import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AuthCard from '../components/AuthCard';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import ReCAPTCHA from 'react-google-recaptcha';
import api from '../config/api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'invalid-site-key';

const Register = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaLoadError, setRecaptchaLoadError] = useState(false);
  const { register, setAuthData } = useAuth();
  const navigate = useNavigate();
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[Register] GOOGLE_CLIENT_ID present:', Boolean(GOOGLE_CLIENT_ID));
    // eslint-disable-next-line no-console
    console.debug('[Register] RECAPTCHA_SITE_KEY present:', Boolean(RECAPTCHA_SITE_KEY));
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/google', {
        idToken: credentialResponse.credential
      });
      
      if (response.data.success) {
        setAuthData(response.data.token, response.data.user);
        toast.success(t('auth.google.success'));
        
        const userRole = response.data.user.role;
        if (userRole === 'ADMIN') {
          navigate('/admin');
        } else if (userRole === 'OWNER') {
          navigate('/dashboard');
        } else if (userRole === 'BARISTA') {
          navigate('/barista');
        } else {
          navigate('/');
        }
      } else {
        toast.error(t('auth.google.failed'));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('auth.google.failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast.error(t('auth.google.failed'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!recaptchaLoadError && !recaptchaToken) {
      toast.error(t('auth.recaptchaRequired'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error(t('common.error'));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t('common.error'));
      return;
    }

    setLoading(true);

    const userData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password
    };

    const result = await register(userData, recaptchaToken);
    
    if (result.success) {
      toast.success(t('common.success'));
      navigate('/onboarding');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <AuthCard subtitle={t('auth.register.subtitle')}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
              {t('auth.register.firstName')}
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="input-field"
              placeholder={t('auth.register.firstName')}
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
              {t('auth.register.lastName')}
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="input-field"
              placeholder={t('auth.register.lastName')}
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
            {t('auth.register.email')}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="input-field"
            placeholder={t('auth.register.email')}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
            {t('auth.register.password')}
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            className="input-field"
            placeholder={t('auth.register.password')}
          />
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
            {t('auth.register.confirmPassword')}
          </label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
            className="input-field"
            placeholder={t('auth.register.confirmPassword')}
          />
        </div>

        <div className="flex justify-center">
          <ReCAPTCHA
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={setRecaptchaToken}
            onExpired={() => setRecaptchaToken(null)}
            onErrored={() => setRecaptchaLoadError(true)}
            theme="dark"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full"
        >
          {loading ? t('common.loading') : t('auth.register.createAccount')}
        </button>
      </form>

      {GOOGLE_CLIENT_ID && (
        <>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#1a1a1a] text-gray-400">{t('auth.orContinueWith')}</span>
            </div>
          </div>

          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="filled_black"
                size="large"
                width="100%"
              />
            </div>
          </GoogleOAuthProvider>
        </>
      )}

      <div className="mt-6 text-center">
        <p className="text-gray-300">
          {t('auth.register.hasAccount')}{' '}
          <Link to="/login" className="text-[#a15507] hover:text-[#924e06] font-medium transition-colors duration-200">
            {t('auth.register.signIn')}
          </Link>
        </p>
      </div>
    </AuthCard>
  );
};

export default Register; 